import { Injectable } from '@angular/core';
import mapboxgl from 'mapbox-gl';
import { BaseEntityLayerManager } from '../core/base/base-entity-layer-manager';
import { IDispositivoAlarma } from 'modelos/src';

/**
 * AlarmasLayerManager
 *
 * Manager para renderizar alarmas domiciliarias en el mapa.
 *
 * Características:
 * - Icono simple (casa-escudo-irix)
 * - Soporta clustering
 * - Color de cluster: rojo (#F44336)
 * - Manager simple sin estados dinámicos
 * - Zoom lejano (< ZOOM_ICONO): círculos en vez del ícono. Son ~6.000 alarmas
 *   y a esa distancia los íconos son una mancha; la capa de símbolos con todas
 *   a la vista trababa el mapa al alejar el zoom, la de círculos cuesta casi
 *   lo mismo que no dibujar nada.
 *
 * Icono: casa-escudo-irix.png (800x800 → escalar a 35px)
 */
@Injectable({
  providedIn: 'root',
})
export class AlarmasLayerManager extends BaseEntityLayerManager<IDispositivoAlarma> {
  // ========== CONFIGURACIÓN ==========
  readonly entityType = 'alarmas';
  readonly supportsClustering = true;
  readonly clusterColor = '#F44336'; // Rojo

  // IDs de layers
  private mainLayerId = 'alarmas-layer';
  private unclusteredLayerId = 'alarmas-unclustered-layer';
  private circleLayerId = 'alarmas-circle-layer';

  // Desde este zoom se dibuja el ícono; más lejos, círculos
  private readonly ZOOM_ICONO = 11;

  // ========== RENDERIZACIÓN ==========

  /**
   * Convierte una alarma a un Feature de GeoJSON
   */
  override entityToFeature(
    alarma: IDispositivoAlarma,
  ): GeoJSON.Feature<GeoJSON.Point> {
    // OPTIMIZACIÓN: Almacenar en mapa en vez de serializar
    this.storeEntity(alarma);

    // Las alarmas tienen domicilio con geojson
    let coordinates: [number, number];

    if (alarma.domicilio?.geojson?.coordinates) {
      coordinates = alarma.domicilio.geojson.coordinates as [number, number];
    } else {
      // Fallback: Buenos Aires
      console.warn(`⚠️ Alarma ${alarma._id} sin coordenadas válidas`);
      coordinates = [-58.443832, -34.587386];
    }

    return {
      type: 'Feature',
      geometry: {
        type: 'Point',
        coordinates,
      },
      properties: {
        id: alarma._id,
        nombre: alarma.nombre,
        // Metadata para popups
        domicilio: alarma.domicilio?.direccion,
        idCliente: alarma.idCliente,
        // NO incluir 'data' - se obtiene del entityMap en clicks
      },
    };
  }

  /**
   * Icono de casa con escudo para alarmas
   */
  override getIconExpression(): string {
    // Versión ya reducida a 40 px (ver ICONS_TO_LOAD): misma imagen que
    // 'alarma-casa-icon', sin el costo de muestrear el PNG de 800 px
    return 'alarma-casa-icon-40';
  }

  // ========== CREACIÓN DE LAYERS ==========

  /**
   * Crea los layers para alarmas
   */
  protected override createLayers(): void {
    if (!this.map) return;

    // 1. LAYERS DE CLUSTERING
    if (this.supportsClustering) {
      this.createStandardClusterLayers();

      // Layer de alarmas NO CLUSTERED
      this.addLayer({
        id: this.unclusteredLayerId,
        type: 'symbol',
        source: this.clusteredSourceId,
        filter: ['!', ['has', 'point_count']],
        layout: {
          'icon-image': this.getIconExpression(),
          'icon-size': 1, // imagen de 40 px, contenido ~26 px visibles (igual a circle-icon de vehículos)
          'icon-allow-overlap': true,
          'icon-padding': 0, // Cajas de colisión más pequeñas
          'icon-ignore-placement': true, // Como vehículos (ver capa principal)
          visibility: 'none',
        },
      });
    }

    // 2. CÍRCULOS (alarmas sin clustering, zoom < ZOOM_ICONO)
    this.addLayer({
      id: this.circleLayerId,
      type: 'circle',
      source: this.sourceId,
      maxzoom: this.ZOOM_ICONO,
      paint: {
        'circle-radius': 5,
        'circle-color': this.clusterColor,
        'circle-stroke-width': 1,
        'circle-stroke-color': '#ffffff',
      },
    });

    // 3. MAIN LAYER (alarmas sin clustering, zoom >= ZOOM_ICONO)
    this.addLayer({
      id: this.mainLayerId,
      type: 'symbol',
      source: this.sourceId,
      minzoom: this.ZOOM_ICONO,
      layout: {
        'icon-image': this.getIconExpression(),
        'icon-size': 1, // imagen de 40 px, contenido ~26 px visibles (igual a circle-icon de vehículos)
        'icon-allow-overlap': true,
        'icon-padding': 0, // Cajas de colisión más pequeñas
        // Como vehículos: con allow-overlap igual se dibujan todas; así
        // además no se cargan ~6.000 cajas al índice de colisión
        'icon-ignore-placement': true,
      },
    });
  }

  // ========== CLUSTERING ==========

  protected override updateClusteringVisibility(): void {
    if (!this.map || !this.supportsClustering) return;

    if (this.clusteringEnabled) {
      // CLUSTERING ON
      // IMPORTANTE: Usar this.isVisible en lugar de true para respetar el estado del filtro
      this.setLayerVisibility(
        `${this.entityType}-cluster-layer`,
        this.isVisible,
      );
      this.setLayerVisibility(
        `${this.entityType}-cluster-count-layer`,
        this.isVisible,
      );
      this.setLayerVisibility(this.unclusteredLayerId, this.isVisible);
      this.setLayerVisibility(this.mainLayerId, false);
      this.setLayerVisibility(this.circleLayerId, false);
    } else {
      // CLUSTERING OFF
      this.setLayerVisibility(`${this.entityType}-cluster-layer`, false);
      this.setLayerVisibility(`${this.entityType}-cluster-count-layer`, false);
      this.setLayerVisibility(this.unclusteredLayerId, false);
      // IMPORTANTE: Usar this.isVisible en lugar de true para respetar el estado del filtro
      this.setLayerVisibility(this.mainLayerId, this.isVisible);
      this.setLayerVisibility(this.circleLayerId, this.isVisible);
    }
  }

  private setLayerVisibility(layerId: string, visible: boolean): void {
    if (!this.map) return;

    if (this.map.getLayer(layerId)) {
      this.map.setLayoutProperty(
        layerId,
        'visibility',
        visible ? 'visible' : 'none',
      );
    }
  }

  // ========== VISIBILIDAD GENERAL ==========

  override setVisibility(visible: boolean): void {
    if (!this.map) return;

    this.isVisible = visible;

    if (this.clusteringEnabled && this.supportsClustering) {
      this.setLayerVisibility(`${this.entityType}-cluster-layer`, visible);
      this.setLayerVisibility(
        `${this.entityType}-cluster-count-layer`,
        visible,
      );
      this.setLayerVisibility(this.unclusteredLayerId, visible);
    } else {
      this.setLayerVisibility(this.mainLayerId, visible);
      this.setLayerVisibility(this.circleLayerId, visible);
    }
  }
}
